import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'

import { HeroKicker } from '@/components/marketing/hero-kicker'
import { BrokerageIcon } from '@/components/marketing/brokerage-icon'
import {
  isBrokerageIntegrationPlaceholder,
  type BrokerageIntegrationEntry,
} from '@/config/marketing-content/brokerage-integrations'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

type SortDirection = 'asc' | 'desc'

function BrokerageIntegrationIcon({
  brokerage,
  className,
  size = 32,
}: {
  brokerage: BrokerageIntegrationEntry
  className?: string
  size?: number
}) {
  return (
    <BrokerageIcon
      slug={brokerage.slug}
      name={brokerage.name}
      iconSlug={brokerage.iconSlug}
      className={cn('brokerage-directory__icon', className)}
      size={size}
    />
  )
}

function BrokerageIntegrationCard({
  brokerage,
  onSelect,
}: {
  brokerage: BrokerageIntegrationEntry
  onSelect: (brokerage: BrokerageIntegrationEntry) => void
}) {
  return (
    <button
      type="button"
      className="brokerage-directory__card brokerage-directory__card--interactive"
      onClick={() => onSelect(brokerage)}
    >
      <div className="brokerage-directory__card-header">
        <BrokerageIntegrationIcon brokerage={brokerage} />
        <span className="brokerage-directory__name">{brokerage.name}</span>
      </div>
      <p className="brokerage-directory__description">{brokerage.description}</p>
    </button>
  )
}

function BrokerageIntegrationDetailDialog({
  brokerage,
  modalEyebrow,
  open,
  onOpenChange,
}: {
  brokerage: BrokerageIntegrationEntry | null
  modalEyebrow: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {brokerage ? (
        <DialogContent
          overlayClassName="bg-black/45 backdrop-blur-sm"
          className="brokerage-detail-dialog flex max-w-[var(--brokerage-detail-dialog-max-width)]"
        >
          <div className="brokerage-detail-dialog__header">
            <BrokerageIntegrationIcon brokerage={brokerage} size={58} className="brokerage-detail-dialog__icon" />
            <div className="brokerage-detail-dialog__heading">
              <HeroKicker className="brokerage-detail-dialog__eyebrow">{modalEyebrow}</HeroKicker>
              <DialogTitle className="brokerage-detail-dialog__title">{brokerage.name}</DialogTitle>
            </div>
          </div>

          <DialogDescription asChild>
            <p className="brokerage-detail-dialog__body">
              {brokerage.detailDescription ?? brokerage.description}
            </p>
          </DialogDescription>

          <a
            href={brokerage.visitUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="brokerage-detail-dialog__visit"
          >
            Visit {brokerage.name}
          </a>
        </DialogContent>
      ) : null}
    </Dialog>
  )
}

export function BrokerageIntegrationsDirectory({
  brokerages,
  searchPlaceholder,
  pageSize,
  modalEyebrow = 'Brokerage Integrations',
  className,
}: {
  brokerages: BrokerageIntegrationEntry[]
  searchPlaceholder: string
  pageSize: number
  modalEyebrow?: string
  className?: string
}) {
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<SortDirection>('asc')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<BrokerageIntegrationEntry | null>(null)
  const directoryTopRef = useRef<HTMLElement>(null)
  const skipPageScrollRef = useRef(true)

  const scrollToDirectoryTop = useCallback(() => {
    directoryTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const goToPage = useCallback(
    (nextPage: number) => {
      setPage(nextPage)
      skipPageScrollRef.current = false
    },
    []
  )

  useEffect(() => {
    if (skipPageScrollRef.current) {
      return
    }
    skipPageScrollRef.current = true
    scrollToDirectoryTop()
  }, [page, scrollToDirectoryTop])

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    let list = brokerages

    if (normalizedQuery) {
      list = list.filter(
        (brokerage) =>
          brokerage.name.toLowerCase().includes(normalizedQuery) ||
          brokerage.description.toLowerCase().includes(normalizedQuery) ||
          brokerage.detailDescription?.toLowerCase().includes(normalizedQuery)
      )
    }

    return [...list].sort((left, right) => {
      const leftPlaceholder = isBrokerageIntegrationPlaceholder(left)
      const rightPlaceholder = isBrokerageIntegrationPlaceholder(right)
      if (leftPlaceholder !== rightPlaceholder) {
        return leftPlaceholder ? 1 : -1
      }

      const comparison = left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })
      return sort === 'asc' ? comparison : -comparison
    })
  }, [brokerages, query, sort])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const pageItems = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  return (
    <section
      ref={directoryTopRef}
      className={cn('brokerage-integrations-directory marketing-section', className)}
    >
      <div className="marketing-container brokerage-directory">
        <div className="brokerage-directory__toolbar">
          <label className="brokerage-directory__search">
            <Search aria-hidden className="brokerage-directory__search-icon" />
            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setPage(1)
              }}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="brokerage-directory__search-input"
            />
          </label>

          <div className="brokerage-directory__sort" role="group" aria-label="Sort integrations">
            <button
              type="button"
              className={cn(
                'brokerage-directory__sort-button',
                sort === 'asc' && 'brokerage-directory__sort-button--active'
              )}
              aria-pressed={sort === 'asc'}
              onClick={() => {
                setSort('asc')
                setPage(1)
              }}
            >
              A-Z
            </button>
            <button
              type="button"
              className={cn(
                'brokerage-directory__sort-button',
                sort === 'desc' && 'brokerage-directory__sort-button--active'
              )}
              aria-pressed={sort === 'desc'}
              onClick={() => {
                setSort('desc')
                setPage(1)
              }}
            >
              Z-A
            </button>
          </div>
        </div>

        {pageItems.length > 0 ? (
          <div className="brokerage-directory__grid">
            {pageItems.map((brokerage) => (
              <BrokerageIntegrationCard
                key={brokerage.slug}
                brokerage={brokerage}
                onSelect={setSelected}
              />
            ))}
          </div>
        ) : (
          <p className="brokerage-directory__empty">No integrations match your search.</p>
        )}

        {totalPages > 1 ? (
          <nav className="brokerage-directory__pagination" aria-label="Integrations pages">
            <button
              type="button"
              className="brokerage-directory__page-button"
              aria-label="Previous page"
              disabled={currentPage <= 1}
              onClick={() => goToPage(Math.max(1, currentPage - 1))}
            >
              <ChevronLeft aria-hidden />
            </button>

            {Array.from({ length: totalPages }, (_, index) => {
              const pageNumber = index + 1
              return (
                <button
                  key={pageNumber}
                  type="button"
                  className={cn(
                    'brokerage-directory__page-button',
                    'brokerage-directory__page-button--number',
                    currentPage === pageNumber && 'brokerage-directory__page-button--active'
                  )}
                  aria-label={`Page ${pageNumber}`}
                  aria-current={currentPage === pageNumber ? 'page' : undefined}
                  onClick={() => goToPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              )
            })}

            <button
              type="button"
              className="brokerage-directory__page-button"
              aria-label="Next page"
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
            >
              <ChevronRight aria-hidden />
            </button>
          </nav>
        ) : null}
      </div>

      <BrokerageIntegrationDetailDialog
        brokerage={selected}
        modalEyebrow={modalEyebrow}
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null)
        }}
      />
    </section>
  )
}
